import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PlanningDetailsItemService from '../../../Services/PlanningDetailsItemService.js';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import PlanningDetailsItem from '../../../Domain/PlanningDetailsItem';
import ShownSubPanelsEnum from '../../../Enums/ShownSubPanelsEnum';
import PlanningSearchParams from '../PlanningItem/PlanningSearchParams';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import PlanningDetailEditWindowDescription from './PlanningDetailEditWindowDescription';
import PlanningDetailEditItem from '../../../Domain/PlanningDetailEditItem';
import PlanningDetailStatusUpdateWindowDescription from './PlanningDetailStatusUpdateWindowDescription';
import PlanningDetailHeader from './PlanningDetailHeader';
import PlanningDetailMassActions from './PlanningDetailMassActions';

function PlanningDetailList({ pricingId = 0 }) {
  const [planningDetailItems, setPlanningDetailItems] = useState([]);
  const [selected, setSelected] = useState(-1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [shownPanel, setShownPanel] = useState(ShownPanelsEnum.Search);
  const [searchItem, setSearchItem] = useState(() => {
    const params = new PlanningSearchParams();
    // Only override URL-derived PricingId if explicitly passed as prop
    if (pricingId) {
      params.PricingId = pricingId;
    }
    return params;
  });

  // Triggers a data refresh by creating a new searchItem reference
  const refreshData = useCallback(() => {
    setSearchItem((prev) => ({ ...prev }));
  }, []);

  const showPanel = useCallback((panelNameEnumId) => {
    if (panelNameEnumId === ShownPanelsEnum.Refresh) {
      panelNameEnumId = ShownPanelsEnum.None;
    }
    setShownPanel((prev) => (panelNameEnumId === prev ? ShownPanelsEnum.None : panelNameEnumId));
  }, []);

  const closePanel = useCallback(() => {
    setShownPanel(ShownPanelsEnum.None);
  }, []);

  const closeSubPanelHandler = useCallback(
    (forceRefresh) => {
      setShownPanel(ShownPanelsEnum.None);
      setSelected(-1);
      setSelectedItem(null);
      if (forceRefresh) {
        refreshData();
      }
    },
    [refreshData]
  );

  const clickOnLink = useCallback((item, panelEnumId) => {
    setSelected(item.PlanningDetailId);
    setSelectedItem(item);
    setShownPanel(panelEnumId);
  }, []);

  const selectAllElements = useCallback(() => {
    setPlanningDetailItems((prev) => {
      const newStatus = prev.some((item) => item.Selected === false);
      return prev.map((item) => ({ ...item, Selected: newStatus }));
    });
  }, []);

  const selectedStatus = useCallback(() => {
    return !planningDetailItems.some((x) => x.Selected === false);
  }, [planningDetailItems]);

  const clickOnSelect = useCallback((item) => {
    setPlanningDetailItems((prev) =>
      prev.map((i) => (i.PlanningDetailId === item.PlanningDetailId ? { ...i, Selected: !i.Selected } : i))
    );
  }, []);

  // Bind static callbacks on PlanningDetailsItem for DataTable row interactions
  useEffect(() => {
    PlanningDetailsItem.clickOnLink = clickOnLink;
    PlanningDetailsItem.clickOnGlobalSelect = selectAllElements;
    PlanningDetailsItem.selectedStatus = selectedStatus;
    PlanningDetailsItem.clickOnSelect = clickOnSelect;
  }, [clickOnLink, selectAllElements, selectedStatus, clickOnSelect]);

  const maxPlannedAssignments = useMemo(() => {
    if (planningDetailItems.length === 0) return 0;
    return Math.max(...planningDetailItems.map((item) => item.NumberOfPlannedAssignments));
  }, [planningDetailItems]);

  const selectedItems = useMemo(() => {
    return planningDetailItems.filter((item) => item.Selected);
  }, [planningDetailItems]);

  const renderShownSubPanel = () => {
    if (selected === -1 || !selectedItem || shownPanel === ShownSubPanelsEnum.None) {
      return null;
    }
    switch (shownPanel) {
      case ShownSubPanelsEnum.EditItem:
        return (
          <GeneralEditWizzardBase
            crudType={CrudTypesEnum.Update}
            editedItem={new PlanningDetailEditItem(selectedItem)}
            clickOnCancel={() => closeSubPanelHandler(false)}
            clickOnSave={() => closeSubPanelHandler(true)}
            windowDescription={PlanningDetailEditWindowDescription}
            errorCode={'PDIEB110'}
          />
        );
      case ShownSubPanelsEnum.StatusUpdate:
        return (
          <GeneralEditWizzardBase
            crudType={CrudTypesEnum.Create}
            editedItem={new PlanningDetailStatusUpdateWindowDescription.itemType([selectedItem])}
            clickOnCancel={() => closeSubPanelHandler(false)}
            clickOnSave={() => closeSubPanelHandler(true)}
            windowDescription={PlanningDetailStatusUpdateWindowDescription}
            approveBeforeSaveCheck={PlanningDetailStatusUpdateWindowDescription.approveBeforeSaveCheck}
            approveBeforeSaveMessage={PlanningDetailStatusUpdateWindowDescription.approveBeforeSaveMessage}
            errorCode={'PDISU114'}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className='contentwrapper'>
      <PlanningDetailHeader
        firstItem={planningDetailItems[0]}
        pricingId={pricingId}
      />
      <PlanningDetailMassActions
        shownPanel={shownPanel}
        showPanel={showPanel}
        closePanel={closePanel}
        refreshData={refreshData}
        selectedItems={selectedItems}
        maxPlannedAssignments={maxPlannedAssignments}
      />
      {renderShownSubPanel()}
      <TabOfDataTable
        type={PlanningDetailsItem}
        shownPanel={shownPanel}
        serviceFunction={PlanningDetailsItemService.GetPlanningDetailsItemsByPricingId}
        convertDBItemsFunction={PlanningDetailsItemService.ConvertDBPlanningDetailsItems}
        showSearchStrip={true}
        searchItem={searchItem}
        itemList={planningDetailItems}
        isFilterDisabled={true}
        forceClearCacheOnSearch={true}
        changeOfItemListFunction={(itemList) => setPlanningDetailItems(itemList)}
        errorCode='PDL271'
      />
    </div>
  );
}

export default PlanningDetailList;
